// Header data for every template (§4.5), lifted verbatim from
// components/Navigation.tsx: the gated built-in links, the per-host-and-sport
// cached `/public/nav-menu` items, refetched on sport change,
// sanitised with safeHref, and the active-link rule.
//
// Classic's Navigation is a view over this hook. The hook's state
// and effects are the ones Navigation has today, in the same order, so when
// Navigation calls it the nav-menu request keeps its timing (§5.2).

import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { api, type NavMenuItem } from '@/services/api'
import { useLeague } from '@/contexts/LeagueContext'
import { ADDON_KEYS, SITE_SECTION_KEYS, isMembersOnly, registrationSiteOn, sectionEnabled, type SiteSectionKey } from '@/contexts/tenantAccess'
import { useSiteContentAllowed } from '../access/context'
import { useSportContext } from '@/contexts/SportContext'
import { safeHref } from '@/utils/safeHref'
import { useSportSwitcher, type SiteSportSwitcher } from './useSportSwitcher'
import { useSiteAccount, type SiteAccountNav } from './useSiteAccount'
import { useDivision, type SiteDivisionSwitcher } from '../division'
import { useStoredSportRestore } from '@/contexts/useStoredSportRestore'
import { gatingFor } from './gating'
import { useLabels } from '../labels'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import type siteCoreEn from '@/i18n/catalogs/site-core/en'
import type { Gating } from '../template/pages'

export interface SiteNavItem {
  /** Stable: the section key for built-ins ('home', 'schedule', …), `menu:<id>` otherwise. */
  key: string
  label: string
  href: string
  external: boolean
  active: boolean
  /**
   * 'form' is never produced since 1.0's Forms were retired at registration
   * step R (the nav menu carries no forms); it stays in the union so the
   * templates' form-link branches still compile (dead, follow-up).
   */
  source: 'section' | 'menu' | 'form'
}

export interface SiteNav {
  leagueId: number | null
  leagueName: string
  logoUrl: string | null
  items: SiteNavItem[]
  sports: SiteSportSwitcher
  /**
   * #583: the division switcher for templates that place it in the chrome
   * (Arena's sub-bar, Huddle's chip rail; Classic keeps it on the page and its
   * nav untouched). `visible` only with 2+ live divisions in the active sport
   * on a list route; Stats offers no "All divisions". The same state as every
   * page VM's `division`.
   */
  divisions: SiteDivisionSwitcher
  /**
   * Section + add-on facts (same as vm.gating), so a template can add gated
   * destinations classic's nav doesn't have (Live, Tickets) only when entitled.
   */
  gating: Gating
  /** Today's active-link rule ('/' exact, else prefix). */
  isActive: (href: string) => boolean
  /**
   * The header's account control (registration plan step 2): null unless the
   * registration add-on is on (strict, fail closed), so templates render it
   * only when set. Signed out: a "Sign in" link; signed in: the member's
   * first name with a small menu (Sign out).
   *
   * With the add-on on, `items` also carries the Register link (key
   * 'register', href '/register', source 'section'; registration plan step
   * 6), after the built-ins and before the menu items. It shows whenever the
   * add-on is on, not only while a registration is open: knowing that would
   * take a member request on every page. A menu or form item already at
   * '/register' wins (no duplicate href).
   */
  account: SiteAccountNav | null
  /**
   * QA E1 (October 30): a members-only league whose visitor is not admitted
   * (signed out, refused, or still being checked). Every league page shows
   * the sign-in wall then, so the chrome offers only what works: the brand,
   * Register (in `items`, when shown) and the account control (Sign in).
   * `items` holds nothing else, `gating` is all off and the sport and
   * division switchers are hidden; templates drop their own fixed
   * destinations too (Live, Tickets, More, the team picker). Always false
   * on a public league and for an admitted member or staff.
   */
  restricted: boolean
}

/** Nav items are per host AND per sport. */
export function navCacheKey(sportId: string | null): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  return `mcl_nav_menu_items_${host}_${sportId ?? 'default'}`
}

/** A built-in link's key: also its label's key in the site-core catalogue (`pages.nav`). */
export type StaticNavKey = keyof typeof siteCoreEn.pages.nav

/** Built-in links; `section` gates them (Venues last, default off). Labels: `pages.nav`. */
export const STATIC_NAV_LINKS: ReadonlyArray<{ key: StaticNavKey; href: string; section?: SiteSectionKey }> = [
  { key: 'home', href: '/' },
  { key: 'schedule', href: '/schedule', section: 'schedule' },
  { key: 'players', href: '/players', section: 'players' },
  { key: 'teams', href: '/teams', section: 'teams' },
  { key: 'stats', href: '/stats', section: 'stats' },
  { key: 'gallery', href: '/gallery', section: 'gallery' },
  { key: 'blog', href: '/blog', section: 'blog' },
  { key: 'venues', href: '/venues', section: 'venues' },
]

export function getCachedNavItems(sportId: string | null): NavMenuItem[] {
  try {
    const cached = localStorage.getItem(navCacheKey(sportId))
    if (cached) return JSON.parse(cached)
  } catch {
    // Ignore parse errors
  }
  return []
}

/** The public Register list (registration add-on only). */
export const REGISTER_HREF = '/register'

/**
 * The link a template keeps in its visible menu (owner, October 30): Register,
 * as core's link or as the admin's own item at /register that replaces it. A
 * template may collapse or wrap the others into More, never this one. It says
 * only where a link goes, not whether it shows: null when no such item is in
 * `items`, and always without the add-on (`account` is null then), so a
 * league without it has nothing pinned.
 */
export function pinnedNavItem(nav: Pick<SiteNav, 'items' | 'account'>): SiteNavItem | null {
  return nav.account ? nav.items.find((i) => i.href === REGISTER_HREF) ?? null : null
}

/**
 * A link to one of 1.0's retired form pages (registration step R): /forms or
 * /forms/... on this site. The server never sends one, but a nav cache written
 * before step R holds the old merged form links (keyed by form id), and it is
 * what shows on first paint and when the menu request fails.
 */
export function isRetiredFormHref(href: string): boolean {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    const url = new URL(href.trim(), origin)
    return url.origin === origin && /^\/forms(\/|$)/i.test(url.pathname)
  } catch {
    return false
  }
}

/** QA E1: nothing to offer a visitor the wall stops (every section and add-on off). */
const CLOSED_GATING: Gating = Object.freeze({
  sections: Object.freeze(Object.fromEntries(SITE_SECTION_KEYS.map((k) => [k, false]))) as Gating['sections'],
  addons: Object.freeze(Object.fromEntries(ADDON_KEYS.map((k) => [k, false]))) as Gating['addons'],
}) as Gating

const isExternal = (href: string) => /^(https?:)?\/\//i.test(href) || /^mailto:|^tel:/i.test(href)

export function useSiteNav(): SiteNav {
  // Restore the fan's stored sport whenever site chrome mounts (P0, §6.0):
  // the trigger moved here from SportSwitcher, so it no longer depends on how
  // (or whether) a template renders the switcher. First hook on purpose: its
  // effect runs before the nav-menu effect, in the same commit, as the
  // switcher children's effects did. Idempotent across multiple callers.
  useStoredSportRestore()
  const words = useT(siteCoreMessages).pages
  const location = useLocation()
  const { league } = useLeague()
  const { activeSportId } = useSportContext()
  // Registration step 33: on a members-only league the menu is league content
  // the gate refuses until the visitor is admitted (the wall's open pages
  // still mount chrome). A context read only (no state, no effect); always
  // true on a public league, so nothing below changes there.
  const allowed = useSiteContentAllowed()
  // A members-only league's menu is never kept in localStorage (the server
  // answers it private, no-store): no read, no write.
  const priv = isMembersOnly(league)
  const privRef = useRef(priv)
  privRef.current = priv
  // Seed from the per-sport cache so the right items appear on first paint.
  const [dynamicNavItems, setDynamicNavItems] = useState<NavMenuItem[]>(() => (allowed && !priv ? getCachedNavItems(activeSportId || null) : []))
  const sports = useSportSwitcher()
  const divisions = useDivision()

  const prevSportRef = useRef(activeSportId)
  // Read through a ref: adding the league to the effect's deps would refetch
  // the menu on every tenant refresh (today it refetches on sport change only).
  const defaultSportRef = useRef(league?.default_sport_id)
  defaultSportRef.current = league?.default_sport_id
  useEffect(() => {
    // Swap to whichever sport's cache exists immediately to kill the flash
    // from the previous sport's items, then refetch fresh.
    //
    // #501: on a first visit the sport is '' (no tenant yet) and the menu is
    // fetched unscoped, which the backend answers with the league's default
    // sport. When the provider then promotes that same default sport, its
    // per-sport cache is still empty: keep the items already shown (they ARE
    // the default sport's) instead of blanking the menu until the refetch.
    const prev = prevSportRef.current
    prevSportRef.current = activeSportId
    // Not admitted on a members-only league: no menu, no request.
    if (!allowed) {
      setDynamicNavItems([])
      return
    }
    const noStore = privRef.current
    const cached = noStore ? [] : getCachedNavItems(activeSportId || null)
    const promotingDefault = !prev && !!activeSportId && activeSportId === defaultSportRef.current
    if (!(promotingDefault && cached.length === 0)) setDynamicNavItems(cached)
    // #566: a response for a sport that is no longer active must not replace
    // the current sport's menu (a cold stored-sport restore fetches the
    // default sport's menu, then the stored one's; they can land in either
    // order). It still belongs in its own sport's cache.
    let stale = false
    api.getNavMenu()
      .then((response) => {
        // Only the menu items: a `forms` array (1.0's form links, retired at
        // registration step R) is ignored even from a CDN-stale payload.
        const merged = [...response.items]
        if (!stale) setDynamicNavItems(merged)
        if (noStore) return
        try {
          localStorage.setItem(navCacheKey(activeSportId || null), JSON.stringify(merged))
        } catch {
          // Ignore storage errors
        }
      })
      .catch((err) => {
        console.error('Failed to load nav menu:', err)
      })
    return () => {
      stale = true
    }
    // `allowed` is the constant true on a public league, so the menu still
    // refetches on sport change only there.
  }, [activeSportId, allowed])

  // After the restore and the nav-menu effect (their order is load-bearing);
  // no state and no effect of its own.
  const account = useSiteAccount()
  // A context read only (no state, no effect): the effect order above holds.
  const registerLabel = useLabels().ui.registration.navLabel

  const isActive = (href: string) => (href === '/' ? location.pathname === '/' : location.pathname.startsWith(href))

  // #260: never hand an unsafe scheme to a link.
  const dynamicItems = dynamicNavItems.flatMap((item): SiteNavItem[] => {
    const href = safeHref(item.href, { relative: true })
    if (!href || isRetiredFormHref(href)) return []
    return [{ key: `menu:${item.id}`, label: item.label, href, external: isExternal(href), active: isActive(href), source: 'menu' as const }]
  })
  // Registration plan step 6: the Register link, only with the strict add-on
  // (`account` is null exactly when the member session is off), so a league
  // without it gets the same items. An admin's own '/register' item wins.
  // Website › Design › Registration's "Register tab in the menu" (menu_tab,
  // on by default) turns it off; an admin's own item is unaffected.
  const register: SiteNavItem[] =
    account && registrationSiteOn(league, 'menu_tab') && !dynamicItems.some((i) => i.href === REGISTER_HREF)
      ? [{ key: 'register', label: registerLabel, href: REGISTER_HREF, external: false, active: isActive(REGISTER_HREF), source: 'section' }]
      : []

  // QA E1: not admitted on a members-only league, every page but Register
  // and sign-in shows the wall: offer only Register (`dynamicItems` is
  // already empty: the menu is never fetched then).
  const restricted = !allowed
  const items: SiteNavItem[] = restricted
    ? register
    : [
        ...STATIC_NAV_LINKS
          .filter((link) => !link.section || sectionEnabled(league, link.section))
          .map((link) => ({ key: link.key, label: words.nav[link.key], href: link.href, external: false, active: isActive(link.href), source: 'section' as const })),
        ...register,
        ...dynamicItems,
      ]

  return {
    leagueId: league?.id ?? null,
    leagueName: league?.name ?? words.leagueFallback,
    logoUrl: league?.logo_url ?? null,
    items,
    sports: restricted ? { ...sports, visible: false } : sports,
    divisions: restricted ? { ...divisions, visible: false } : divisions,
    gating: restricted ? CLOSED_GATING : gatingFor(league),
    isActive,
    account,
    restricted,
  }
}

